import {
  CompanySelectDto,
  CompanyType,
  IntelCompanyDetailDto,
  ResearchOrganizationFundingType,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { AvatarList } from '@components/shared/avatar-list.component';
import { BadgeList } from '@components/shared/badge-list.component';
import { CompanyLogoPreview } from '@components/shared/company-logo-preview.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { Tile } from '@components/shared/tile.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { cn } from '@lib/utils';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import { MILITARY_SERVICE_COUNTRY_TO_CODE, countries } from '@utils/constants';
import { displayLocation } from '@utils/functions.util';
import { formatProper, formatUsd } from '@utils/text.util';
import { isValidUrl, toHttpsUrl } from '@utils/url.util';
import { ArrowRight, BuildingIcon, EditIcon } from 'lucide-react';
import { FunctionComponent, ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cmmcLevelOptions } from '../../../../utils/constants';
import { CompanyProfileModal } from './company-company-profile-modal.component';

type CompanyInfoCardProps = {
  action?: 'edit' | 'none' | 'redirect';
  company?: CompanySelectDto | IntelCompanyDetailDto;
  showDetails?: boolean;
  showEmptyState?: boolean;
  variant?: 'accordion' | 'expanded';
};

const isDefined = <T,>(value: T | null | undefined): value is T => value != null;

const CompanyLogo = (props: { logoUrl?: string; onClick?: () => void }) => {
  const { logoUrl, onClick } = props;

  return (
    <CompanyLogoPreview
      className={cn('h-14 w-14 rounded-xl', onClick && 'cursor-pointer hover:bg-gray-200 transition-colors')}
      fallback={<BuildingIcon className="size-5 text-gray-600" />}
      logoAlt="Company logo"
      logoUrl={logoUrl}
      onClick={onClick}
    />
  );
};

const CompanyDetails = ({
  city,
  country,
  name,
  onClick,
  slug,
  state,
}: {
  city?: string;
  country?: string;
  name?: string;
  onClick?: () => void;
  slug?: string;
  state?: string;
}) => (
  <div
    className={cn('flex flex-col gap-1', onClick && 'cursor-pointer')}
    onClick={onClick}
  >
    <div className={cn('font-medium', onClick && 'hover:underline')}>
      {name}
      {slug && <span className="text-gray-500 font-normal"> ({slug})</span>}
    </div>
    <div className="text-sm text-gray-500">{displayLocation({ city, countryCode: country, state })}</div>
  </div>
);

const CompanyInfoText = ({
  label,
  value,
  fullWidth = false,
}: {
  fullWidth?: boolean;
  label: string;
  value?: ReactNode;
}) => {
  const resolvedValue = value ?? '—';
  const isPlainValue = typeof resolvedValue === 'string' || typeof resolvedValue === 'number';

  return (
    <div
      className={cn('flex flex-col gap-1 min-w-0 overflow-hidden', fullWidth ? 'w-full' : 'w-full @[40rem]:w-[45%]')}
    >
      <span className="font-light text-[#1F1C1AB2]">{label}</span>
      <div
        className={cn(
          'w-full font-normal text-[#1F1C1ACC] min-w-0 overflow-hidden',
          isPlainValue && !fullWidth && 'text-nowrap text-ellipsis',
          isPlainValue && fullWidth && 'line-clamp-12',
        )}
      >
        {resolvedValue}
      </div>
    </div>
  );
};

const companyFinancialDataExists = (company?: CompanySelectDto | IntelCompanyDetailDto) => {
  if (!company) return false;
  return Boolean(company.revenue?.amount || company.revenue?.valuation);
};

const companyUrlsExist = (company?: CompanySelectDto | IntelCompanyDetailDto) => {
  if (!company) return false;
  return Boolean(company.urls?.website || company.urls?.twitter || company.urls?.linkedin || company.urls?.crunchbase);
};

const companyInfoFields = [
  { label: 'Legal Name', value: (company: CompanySelectDto | IntelCompanyDetailDto) => company?.name },
  { label: 'Year Founded', value: (company: CompanySelectDto | IntelCompanyDetailDto) => company?.yearFounded },
  { label: 'SAMS UEI Number', value: (company: CompanySelectDto | IntelCompanyDetailDto) => company?.samsUeiNumber },
  {
    label: 'CMMC Level',
    value: (company: CompanySelectDto | IntelCompanyDetailDto) =>
      cmmcLevelOptions.find((option) => option.value === company?.cmmcLevel)?.label,
  },
  { label: 'Description', value: (company: CompanySelectDto | IntelCompanyDetailDto) => company?.description },
];

const financialFields = [
  {
    label: 'Revenue',
    value: (company: CompanySelectDto | IntelCompanyDetailDto) =>
      company?.revenue?.amount && company?.revenue?.period
        ? `${formatUsd(company.revenue.amount, 0)} USD ${String(company.revenue.period).toLowerCase()}`
        : null,
  },
  {
    label: 'Valuation',
    value: (company: CompanySelectDto | IntelCompanyDetailDto) =>
      company?.revenue?.valuation ? `${formatUsd(company.revenue.valuation, 0)} USD` : null,
  },
];

const urlFields = [
  { label: 'Website', value: (company: CompanySelectDto | IntelCompanyDetailDto) => company?.urls?.website },
  { label: 'Twitter', value: (company: CompanySelectDto | IntelCompanyDetailDto) => company?.urls?.twitter },
  { label: 'LinkedIn', value: (company: CompanySelectDto | IntelCompanyDetailDto) => company?.urls?.linkedin },
  { label: 'Crunchbase', value: (company: CompanySelectDto | IntelCompanyDetailDto) => company?.urls?.crunchbase },
];

const mapCountryCodeToName = (code?: string) => {
  if (!code) return undefined;
  const normalizedCode = code.toUpperCase();
  const countryMatch = countries.find((country) => country.code === normalizedCode || country.name === code);
  return countryMatch?.name ?? code;
};

const formatFundingCountries = (codes?: string[]) => {
  if (!codes || codes.length === 0) return '—';
  const names = codes.map((code) => mapCountryCodeToName(code)).filter((name): name is string => Boolean(name));
  if (names.length === 0) return '—';
  return (
    <ul className="list-disc pl-4">
      {names.map((name) => (
        <li key={name}>{name}</li>
      ))}
    </ul>
  );
};

const formatContact = (contact?: { name?: string; email?: string }) => {
  if (!contact) return '—';
  const { name, email } = contact;
  if (!name && !email) return '—';

  return (
    <div className="flex flex-col">
      {name && <span>{name}</span>}
      {email && (
        <a
          href={`mailto:${email}`}
          rel="noreferrer noopener"
          className="hover:underline text-blue-600"
        >
          {email}
        </a>
      )}
    </div>
  );
};

const TextFields = ({ company }: { company?: CompanySelectDto | IntelCompanyDetailDto }) => {
  const governmentWorkDisplay = useMemo(() => {
    const govCountryCodes = company?.previousGovernmentWorkCountries;
    const govServices = company?.previousGovernmentWorkServices;

    if (!govCountryCodes || govCountryCodes.length === 0) {
      return null;
    }

    const countryLabels = govCountryCodes.map((code) => countries.find((c) => c.code === code)?.name).filter(isDefined);

    const serviceAvatarItems = (govServices ?? []).map((service) => {
      const parentName = service.parentService?.name;
      const countryCode = parentName ? MILITARY_SERVICE_COUNTRY_TO_CODE[parentName] || parentName : '';
      const tooltip = countryCode ? `${countryCode}: ${service.name}` : service.name;

      return {
        altText: service.name,
        imageUrl: service.slug ? `/images/services/${service.slug}.png` : undefined,
        initialText: service.name,
        tooltip,
      };
    });

    return {
      countries: countryLabels,
      serviceAvatars: serviceAvatarItems,
    };
  }, [company?.previousGovernmentWorkCountries, company?.previousGovernmentWorkServices]);

  if (!company) return null;

  const isResearch = company.type === CompanyType.RESEARCH || company.type === CompanyType.RESEARCH_PROJECT;

  const renderPreviousGovernmentWork = () => {
    if (!governmentWorkDisplay) return null;

    return (
      <div className="flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid">
        <CompanyInfoText
          label="Previous Government Work"
          value={
            <BadgeList
              items={governmentWorkDisplay.countries.map((name) => ({ id: name, name }))}
              size="sm"
            />
          }
          fullWidth
        />
        {governmentWorkDisplay.serviceAvatars.length > 0 && (
          <CompanyInfoText
            label="Military Services"
            value={
              <AvatarList
                items={governmentWorkDisplay.serviceAvatars}
                limit={5}
                size="sm"
              />
            }
            fullWidth
          />
        )}
      </div>
    );
  };

  if (!isResearch) {
    return (
      <div className="flex flex-col">
        <div className="flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid">
          {companyInfoFields.map(({ label, value }) => {
            const fieldValue = value(company);
            return fieldValue ? (
              <CompanyInfoText
                key={label}
                label={label}
                value={fieldValue}
                fullWidth={label === 'Description'}
              />
            ) : null;
          })}
        </div>

        {companyFinancialDataExists(company) && (
          <div className="flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid">
            {financialFields.map(({ label, value }) => {
              const fieldValue = value(company);
              return fieldValue ? (
                <CompanyInfoText
                  key={label}
                  label={label}
                  value={fieldValue}
                />
              ) : null;
            })}
          </div>
        )}

        {renderPreviousGovernmentWork()}

        {companyUrlsExist(company) && (
          <div className="flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid">
            {urlFields.map(({ label, value }) => {
              const url = value(company);
              if (!url || !isValidUrl(url)) return null;
              const normalizedUrl = toHttpsUrl(url);
              return (
                <CompanyInfoText
                  key={label}
                  label={label}
                  value={
                    <ExternalLink
                      className="max-w-full"
                      href={normalizedUrl}
                      iconClassName="shrink-0"
                      iconSize={16}
                      title={normalizedUrl}
                    >
                      {normalizedUrl}
                    </ExternalLink>
                  }
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const researchData = company.researchOrganizationData;
  const organizationTypeLabel = researchData?.type
    ? formatProper(researchData.type.toLowerCase().replace(/_/g, ' '))
    : undefined;
  const locationLabel = displayLocation({
    city: company.city,
    countryCode: company.country,
    state: company.state,
  });
  const departmentContact = formatContact(researchData?.departmentHeadContact);
  const techTransferContact = formatContact(researchData?.techTransferContact);
  const ipLicenseValue = researchData?.ipLicense === undefined ? '—' : researchData.ipLicense ? 'Yes' : 'No';
  const foreignAffiliates = (researchData?.foreignAffiliates || []).filter(
    (affiliate) => affiliate && affiliate.trim().length > 0,
  );
  const foreignAffiliation = foreignAffiliates.length > 0;
  const foreignAffiliationsValue = foreignAffiliation ? (
    <BadgeList
      items={foreignAffiliates.map((affiliate) => ({ id: affiliate, name: affiliate }))}
      size="sm"
    />
  ) : (
    'No'
  );
  const fundingType = researchData?.funding?.type;
  const hasFunding = Boolean(fundingType);
  const fundingTypeLabel = fundingType ? formatProper(fundingType.toLowerCase().replace(/_/g, ' ')) : undefined;
  const isForeignFunding = fundingType === ResearchOrganizationFundingType.FOREIGN_NATION;
  const fundingCountryBadges = (researchData?.funding?.countries || [])
    .map((code) => {
      const label = mapCountryCodeToName(code) ?? code;
      return { id: code, name: label };
    })
    .filter((item) => Boolean(item.name));
  const fundingCountriesValue =
    fundingCountryBadges.length > 0 ? (
      <BadgeList
        items={fundingCountryBadges}
        size="sm"
      />
    ) : (
      '—'
    );

  return (
    <div className="flex flex-col">
      <div className="flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid">
        <CompanyInfoText
          label="Legal Name"
          value={company.name}
        />
        <CompanyInfoText
          label="Organization Type"
          value={organizationTypeLabel}
        />
        <CompanyInfoText
          label="Location"
          value={locationLabel || undefined}
        />
        <CompanyInfoText
          label="Department"
          value={researchData?.departmentName}
        />
        <CompanyInfoText
          label="Description"
          value={company.description}
          fullWidth
        />
      </div>

      <div className="flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid">
        <CompanyInfoText
          label="Department Contact"
          value={departmentContact}
        />
        <CompanyInfoText
          label="Tech Transfer Contact"
          value={techTransferContact}
        />
      </div>

      <div className="flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid">
        <CompanyInfoText
          label="Intellectual Property License Established"
          value={ipLicenseValue}
        />
        <CompanyInfoText
          label="Foreign Affiliations"
          value={foreignAffiliationsValue}
          fullWidth={foreignAffiliation}
        />
        <CompanyInfoText
          label="Funding"
          value={hasFunding ? 'Yes' : 'No'}
        />
        {hasFunding && (
          <CompanyInfoText
            label="Funding Type"
            value={fundingTypeLabel}
          />
        )}
        {hasFunding && isForeignFunding && (
          <CompanyInfoText
            label="Funding Countries"
            value={fundingCountriesValue}
            fullWidth
          />
        )}
      </div>

      {renderPreviousGovernmentWork()}

      {companyUrlsExist(company) && (
        <div className="flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid">
          {urlFields.map(({ label, value }) => {
            const url = value(company);
            if (!url || !isValidUrl(url)) return null;
            const normalizedUrl = toHttpsUrl(url);
            return (
              <CompanyInfoText
                key={label}
                label={label}
                value={
                  <ExternalLink
                    className="max-w-full"
                    href={normalizedUrl}
                    iconClassName="shrink-0"
                    iconSize={16}
                    title={normalizedUrl}
                  >
                    {normalizedUrl}
                  </ExternalLink>
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const CompanyDetailsTile: FunctionComponent<CompanyInfoCardProps> = (props) => {
  const { action = 'edit', company, showDetails = true, showEmptyState = true, variant = 'expanded' } = props;

  const { isPending } = useGetCurrentUser();
  const navigate = useNavigate();

  const [isCompanyProfileModalOpen, setIsCompanyProfileModalOpen] = useState(false);

  const handleCloseCompanyProfileModal = () => setIsCompanyProfileModalOpen(false);

  const isEmpty = showEmptyState && !company;

  const handleRedirect = () => {
    navigate(routes.organization.path);
  };

  const handleEdit = () => {
    setIsCompanyProfileModalOpen(true);
  };

  const hasAction = action !== 'none';
  const handleAction = action === 'redirect' ? handleRedirect : handleEdit;
  const isRedirect = action === 'redirect';
  const handleLogoAndNameClick = hasAction ? handleAction : undefined;
  const modalCompany = action === 'edit' && company && 'invites' in company ? company : undefined;

  const headerContent = (
    <div className={cn('relative flex min-h-10 items-start', hasAction && 'pr-12')}>
      <div className="flex min-w-0 flex-row items-center gap-4">
        <CompanyLogo
          logoUrl={company?.logoUrl}
          onClick={handleLogoAndNameClick}
        />
        <CompanyDetails
          city={company?.city}
          country={company?.country}
          name={company?.name}
          onClick={handleLogoAndNameClick}
          slug={company?.slug ?? undefined}
          state={company?.state}
        />
      </div>
      <div
        className={cn(
          'absolute right-0 top-0 flex items-end',
          variant === 'accordion' ? 'flex-col gap-2' : 'items-start',
        )}
      >
        {hasAction && (
          <IconButton.HoverReveal
            ariaLabel={isRedirect ? 'View Organization' : 'Update Organization'}
            icon={isRedirect ? <ArrowRight /> : <EditIcon />}
            onClick={handleAction}
            variant="secondary"
          />
        )}
        {variant === 'accordion' && (
          <AccordionTrigger className="py-0 h-auto w-auto p-0 hover:no-underline [&>svg]:h-4 [&>svg]:w-4" />
        )}
      </div>
    </div>
  );

  return (
    <Tile
      className="min-h-[88px]"
      emptyState={{
        message: 'Add your Company now',
        title: 'Company Missing',
      }}
      hoverReveal
      isEmpty={isEmpty}
      isLoading={isPending}
    >
      <div className="@container flex flex-col gap-4">
        {variant === 'accordion' ? (
          <Accordion
            type="single"
            collapsible
            className="w-full"
          >
            <AccordionItem
              value="details"
              className="border-none"
            >
              <div className="flex flex-col gap-4">
                {headerContent}
                {showDetails && (
                  <AccordionContent className="pt-0 pb-0">
                    <TextFields company={company} />
                  </AccordionContent>
                )}
              </div>
            </AccordionItem>
          </Accordion>
        ) : (
          <>
            {headerContent}
            {showDetails && <TextFields company={company} />}
          </>
        )}
        {action === 'edit' && (
          <CompanyProfileModal
            company={modalCompany}
            isEdit={true}
            isOpen={isCompanyProfileModalOpen}
            onClose={handleCloseCompanyProfileModal}
            onCompanyCreated={() => {}}
            onContinue={() => {}}
          />
        )}
      </div>
    </Tile>
  );
};
